import { action, computed, observable } from 'mobx'
import { filter, switchMap } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { type UserData } from '@/features/iam/users/UserData'
import { type Consultation } from '@/features/jobs/consultations/Consultation'
import { type ConsultationStore } from '@/features/jobs/consultations/ConsultationStore'
import type { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { dateFormatters } from '@/utils/date-util'

/**
 * A consultation as the preview lists it.
 */
export interface UpcomingConsultation {
  clientDisplayName: string
  consultationStartDate: string
  jobId: Id
}

/**
 * Width a call takes up in the expanded preview.
 */
const callItemWidth = 360

/**
 * Horizontal padding of the preview row.
 */
const rowPadding = 48

/**
 * Previews a pro's next consultations above the job list.
 */
export class UpcomingConsultationPreviewViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether the upcoming consultation preview is expanded.
   */
  @observable
  accessor isExpanded = false

  /**
   * The maximum amount of upcoming calls to display in the preview.
   */
  @observable
  accessor maxCallItems = 3

  /**
   * Array of at most the next three scheduled consultations.
   */
  @observable
  accessor bookedConsultations: Consultation[] = []

  /**
   * Refreshes the consultations whenever the preview is expanded.
   */
  private readonly _fetchOnExpand$ = toStream(() => this.isExpanded).pipe(
    filter(Boolean),
    switchMap(() =>
      withAbortSignal((signal) => this._fetchConsultations(signal)),
    ),
  )

  constructor(
    private readonly _consultationStore: ConsultationStore,
    private readonly _getUserData: () => UserData | null,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(this._fetchOnExpand$)
  }

  /**
   * Whether the user is a pro.
   */
  @computed
  get isPro() {
    return this._getUserData()?.userDataType === 'Pro'
  }

  /**
   * Filtered array of at most the next three scheduled consultations.
   */
  @computed
  get filteredConsultations(): UpcomingConsultation[] {
    const max = this.maxCallItems > 0 ? this.maxCallItems : 1

    return (
      this.bookedConsultations
        // Filter out start times earlier than present time plus 15 minutes.
        // AKA don't show consultations that started 15 or more minutes ago.
        .filter(
          (c) =>
            !!c.bookedTime?.startTime &&
            c.bookedTime.startTime.valueOf() >= Date.now() + 900000,
        )
        .sort((a, b) => {
          return (
            a.bookedTime!.startTime.valueOf() -
            b.bookedTime!.startTime.valueOf()
          )
        })
        .slice(0, max)
        .map((c) => {
          return {
            clientDisplayName: c.clientDisplayName,
            consultationStartDate: dateFormatters.dateTimeWithoutYear.format(
              c.bookedTime!.startTime,
            ),
            jobId: c.jobId,
          }
        })
    )
  }

  /**
   * Whether there are any upcoming consultations.
   */
  @computed
  get areConsultationsUpcoming() {
    return this.filteredConsultations.length > 0
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this._fetchConsultations(this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this._reset()
  }

  /**
   * Toggles isExpanded.
   */
  @action.bound
  toggleIsExpanded() {
    this.isExpanded = !this.isExpanded
  }

  /**
   * Fits as many calls as the preview row has room for.
   */
  @action.bound
  setRowWidth(width: number) {
    this.maxCallItems = Math.floor((width - rowPadding) / callItemWidth)
  }

  @action.bound
  private _reset() {
    this.isExpanded = false
    this.maxCallItems = 3
    this.bookedConsultations = []
  }

  private async _fetchConsultations(signal: AbortSignal) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading upcoming consultations failed',
      signal,
    )
    const bookedConsultations = await withRetry(() =>
      this._consultationStore.fetchBookedConsultations(signal),
    ).catch(() => null)
    if (bookedConsultations) {
      this._setBookedConsultations(bookedConsultations)
    }
  }

  @action.bound
  private _setBookedConsultations(consultations: Consultation[]) {
    this.bookedConsultations = consultations
  }
}
